/**
 * apps/command/src/App.tsx — routing, and §15.7's role-adaptive home.
 *
 * ============================================================================
 * THE LANDING IS A ROUTE PER ROLE, NOT A CAPABILITY LOOKUP.
 * ============================================================================
 *
 * The murojaat panel's `LandingRedirect` walks a capability list, and that is
 * right for it: it is answering "what is the first screen this account can
 * reach". §15.7 is answering a different question — "where does this person's
 * work begin" — and the two give different answers for the same account. An
 * operator is perfectly PERMITTED to read the executive home; they should
 * simply not start there.
 *
 * So `mbtHomeRoute()` in `@navoiy/domain` maps role → route, and `/` renders
 * the governor's home for a governor and REDIRECTS everyone else to theirs.
 * Every route still carries its own capability gate underneath, because the
 * landing rule is about ergonomics and the gates are about authority — and
 * conflating those is how a redirect becomes an access control by accident.
 *
 * ============================================================================
 * `mbt.access` IS CHECKED ONCE, AT THE DOOR.
 * ============================================================================
 *
 * An account that signs in successfully but holds no MBT capability — a
 * `handler`, say, who typed the wrong URL — gets a plain refusal rather than an
 * empty console. Their session is untouched and the link back to the murojaat
 * panel is in the top bar, which is where their work actually is.
 */
import { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { mbtHomeRoute, type AdminCapability } from '@navoiy/domain';
import { SessionProvider, useSession } from './auth/SessionContext.js';
import { AppShell } from './components/AppShell.jsx';
import { t } from './i18n/index.js';
import { AlertsScreen } from './screens/AlertsScreen.jsx';
import { AdsScreen } from './screens/AdsScreen.js';
import { CameraWallScreen } from './screens/CameraWallScreen.js';
import { RiskScreen } from './screens/RiskScreen.js';
import { OperativMapScreen } from './screens/OperativMapScreen.js';
import { ScreenWallScreen } from './screens/ScreenWallScreen.js';
import { StaffScreen } from './screens/StaffScreen.js';
import { BroadcastScreen } from './screens/BroadcastScreen.js';
import { BulkScreen } from './screens/BulkScreen.js';
import { ChannelsScreen } from './screens/ChannelsScreen.jsx';
import { DavomatScreen } from './screens/DavomatScreen.js';
import { FleetGrid } from './screens/FleetGrid.jsx';
import { GovernorHome } from './screens/GovernorHome.jsx';
import { KioskDetail } from './screens/KioskDetail.jsx';
import { LoginScreen } from './screens/LoginScreen.jsx';
import { OperatorConsole } from './screens/OperatorConsole.jsx';
import { PhasePlaceholder } from './screens/PhasePlaceholder.jsx';
import { RemediationScreen } from './screens/RemediationScreen.jsx';
import { TechnicianQueue } from './screens/TechnicianQueue.jsx';
import { Notice, Panel } from './ui/index.jsx';

/**
 * «Shahar xaritasi» is the ONE lazily-loaded route, and platform-spec §7 3.6 is
 * why: MapLibre is ~1.08 MB minified and, imported statically, it is parsed at
 * boot and held for the life of the tab — on every screen in this panel,
 * including the ones an operator actually lives on. It tripled the entry chunk.
 *
 * Loaded on navigation instead, it costs the reader who opens the map a single
 * fetch from the same origin, and costs everybody else nothing. The precedent
 * is the kiosk's own face scanner, which lazy-loads face-api for the same
 * reason.
 */
const XaritaScreen = lazy(async () => ({ default: (await import('./screens/XaritaScreen.js')).XaritaScreen }));

/** Wraps a route in its capability gate. The server enforces it too. */
function Require({
  capability,
  children,
}: {
  capability: AdminCapability;
  children: React.JSX.Element;
}): React.JSX.Element {
  const { allows } = useSession();
  if (!allows(capability)) return <Notice tone="crit">{t('error.forbidden')}</Notice>;
  return children;
}

/**
 * `/` is the governor's home for a governor, and a redirect for everybody else.
 *
 * Written as its own component rather than a wrapper so the "no" branch can
 * REDIRECT rather than render a refusal: `/` is where the panel lands after
 * sign-in, and an operator must not be greeted by an error on a screen they
 * never asked for. It cannot loop — `mbtHomeRoute` returns `/` only for a
 * governor, which is the branch that renders.
 */
function HomeRoute(): React.JSX.Element {
  const { me } = useSession();
  if (!me) return <Navigate to="/" replace />;
  const home = mbtHomeRoute(me.role);
  return home === '/' ? <GovernorHome /> : <Navigate to={home} replace />;
}

function AuthenticatedApp(): React.JSX.Element {
  const { status, allows } = useSession();

  if (status === 'loading') {
    return (
      <div className="flex h-full items-center justify-center bg-canvas text-label text-ink-500">
        {t('app.loading')}
      </div>
    );
  }
  if (status === 'anonymous') return <LoginScreen />;

  // The door (§1.3 / the `mbt.access` capability).
  if (!allows('mbt.access')) {
    return (
      <AppShell>
        <Panel className="px-6 py-10">
          <Notice tone="crit">{t('error.forbidden')}</Notice>
        </Panel>
      </AppShell>
    );
  }

  return (
    <Routes>
      {/*
        MBT-X §1 — «Shahar xaritasi» renders its OWN shell, outside the layout
        route, because it is the one screen that needs `bleed`: the layout route
        renders `<AppShell />` with no props and a child route cannot reach up
        to set one.

        It is still the same shell — rail, top bar, `Esc`, the lot — so this is
        a different INSTANCE, not a different frame. Route ranking picks the
        literal `/xarita` over anything the layout route matches, so the order
        here is documentation rather than behaviour.

        Behind `mbt.overview.read`: the map is an overview surface (§15.7), it
        carries no kiosk status and no citizen record, and the operator socket
        gates `vehicles.subscribe` on the same capability.
      */}
      <Route
        path="/xarita"
        element={
          <Require capability="mbt.overview.read">
            <AppShell bleed title={t('xarita.title')}>
              {/* The fallback is the map's own black region, not a spinner
                  (§15.12): the chunk arrives in a frame or two on the same
                  origin, and a flash of the page background followed by black
                  is a worse transition than black straight through. */}
              <Suspense fallback={<div className="h-full w-full bg-map-void" />}>
                <XaritaScreen />
              </Suspense>
            </AppShell>
          </Require>
        }
      />
      <Route element={<AppShell />}>
        <Route path="/" element={<HomeRoute />} />
        <Route
          path="/console"
          element={
            <Require capability="mbt.fleet.read">
              <OperatorConsole />
            </Require>
          }
        />
        <Route
          path="/fleet"
          element={
            <Require capability="mbt.fleet.read">
              <FleetGrid />
            </Require>
          }
        />
        {/* Before `/fleet/:deviceId` would matter — there is no literal segment
            to collide with today, but the ordering is kept explicit so adding
            `/fleet/new` later cannot be read as a device id. */}
        <Route
          path="/fleet/:deviceId"
          element={
            <Require capability="mbt.fleet.read">
              <KioskDetail />
            </Require>
          }
        />
        {/*
          One row per app surface — kiosk, mobile, bot, Mini App. Behind
          `mbt.overview.read`, same as `/` and `/broadcast`: aggregates only,
          no citizen record behind any figure on it.
        */}
        <Route
          path="/channels"
          element={
            <Require capability="mbt.overview.read">
              <ChannelsScreen />
            </Require>
          }
        />
        {/*
          §15.7's "technician view — the queue. Assigned alerts first, kiosk
          health, diagnostics, rollout state." P1 delivers the queue itself;
          diagnostics and rollout arrive with the commands and update work.
        */}
        <Route
          path="/queue"
          element={
            <Require capability="mbt.fleet.read">
              <TechnicianQueue />
            </Require>
          }
        />
        {/*
          §16 screen 8. Separate from the feed on purpose: resolving requires a
          note (§4.4), and a one-click resolve in a list is how a queue gets
          emptied without anything being fixed.
        */}
        <Route
          path="/alerts"
          element={
            <Require capability="mbt.fleet.read">
              <AlertsScreen />
            </Require>
          }
        />
        <Route
          path="/remediation"
          element={
            <Require capability="mbt.fleet.read">
              <RemediationScreen />
            </Require>
          }
        />
        {/*
          §4.8's bulk operations. Behind `mbt.fleet.read` rather than
          `mbt.command.issue`, because the plan QUEUE is the thing a second
          person has to be able to find — and a technician standing at a kiosk
          that is about to be rebooted has the strongest claim of anyone to see
          what is pending. The controls inside the screen are gated separately.
        */}
        <Route
          path="/bulk"
          element={
            <Require capability="mbt.fleet.read">
              <BulkScreen />
            </Require>
          }
        />
        <Route
          path="/mahalla"
          element={
            <Require capability="mbt.fleet.read">
              <PhasePlaceholder noteKey="phase.mahalla" />
            </Require>
          }
        />
        <Route
          path="/broadcast"
          element={
            <Require capability="mbt.overview.read">
              <BroadcastScreen />
            </Require>
          }
        />
        <Route
          path="/risk"
          element={
            <Require capability="mbt.fleet.read">
              <RiskScreen />
            </Require>
          }
        />
        <Route
          path="/camera"
          element={
            <Require capability="mbt.live.view">
              <CameraWallScreen />
            </Require>
          }
        />
        <Route
          path="/staff"
          element={
            <Require capability="mbt.staff.manage">
              <StaffScreen />
            </Require>
          }
        />
        <Route
          path="/screens"
          element={
            <Require capability="mbt.live.view">
              <ScreenWallScreen />
            </Require>
          }
        />
        <Route
          path="/map"
          element={
            <Require capability="mbt.fleet.read">
              <OperativMapScreen />
            </Require>
          }
        />
        <Route
          path="/ads"
          element={
            <Require capability="mbt.ads.manage">
              <AdsScreen />
            </Require>
          }
        />
        <Route
          path="/davomat"
          element={
            <Require capability="mbt.attendance.read">
              <DavomatScreen />
            </Require>
          }
        />
        {/* Anything else returns to this account's own home rather than to a
            404. There is no URL in this panel worth explaining to an official. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App(): React.JSX.Element {
  return (
    <SessionProvider>
      {/* §2.1 — the SPA is served at `<admin-host>/markaziy/`, so the router's
          basename must match Vite's `base`. Without it every in-app link would
          drop the prefix and land on the murojaat panel. */}
      <Router basename="/markaziy">
        <AuthenticatedApp />
      </Router>
    </SessionProvider>
  );
}
